import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faSpinner, faPlus, faChevronLeft,
    faExclamationTriangle, faCheckCircle,
    faFileAlt, faTrash,
    faBoxOpen, faStore, faTimes,
    faChevronDown, faChevronUp,
    faClipboardList, faLayerGroup,
    faTrophy, faFilePdf, faHashtag,
} from '@fortawesome/free-solid-svg-icons'
import PageHeader from '../../utils/PageHeader'
import { API_URL } from '../../utils/Constants/URLS'
import '../../../css/purchase.css'
import '../../../css/common.css'

/* ─────────────────────────────────────────────
   API helpers
───────────────────────────────────────────── */
const authHeaders = (token) => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
})

const fetchPurchaseRequests = async (token, params = {}) => {
    const qs = []
    // encodeURIComponent uses %20 for spaces (not +) — better for backend search parsers
    if (params.search) qs.push(`search=${encodeURIComponent(params.search)}`)
    if (params.approvalStatus && params.approvalStatus !== 'all')
        qs.push(`approvalStatus=${encodeURIComponent(params.approvalStatus)}`)
    if (params.page) qs.push(`page=${encodeURIComponent(params.page)}`)

    const res = await fetch(
        `${API_URL}purchase-requests?limit=5&${qs.join('&')}&sortBy=prId&approvalStatus=approved`,
        { method: 'GET', headers: authHeaders(token) }
    )
    if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.message || `Error ${res.status}`)
    }
    return res.json()
}

/* ─────────────────────────────────────────────
   Small UI atoms
───────────────────────────────────────────── */
const Badge = ({ children, color = 'gray' }) => {
    const colors = {
        gray: 'bg-gray-100 text-gray-600',
        green: 'bg-green-100 text-green-700',
        yellow: 'bg-yellow-100 text-yellow-700',
        red: 'bg-red-100 text-red-700',
        purple: 'bg-purple-100 text-purple-700',
        blue: 'bg-blue-100 text-blue-700',
    }
    return (
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors[color]}`}>
            {children}
        </span>
    )
}

const OemBadge = ({ isOem }) => (
    <span
        className={
            isOem
                ? 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-50 border border-purple-200 text-purple-700 whitespace-nowrap'
                : 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-50 border border-gray-200 text-gray-600 whitespace-nowrap'
        }
    >
        {isOem ? 'OEM' : 'Non-OEM'}
    </span>
)

const statusColor = (s = '') => {
    if (!s) return 'gray'
    const l = s.toLowerCase()
    if (l.includes('approved')) return 'green'
    if (l.includes('pending')) return 'yellow'
    if (l.includes('reject')) return 'red'
    return 'gray'
}

/* ─────────────────────────────────────────────
   Step indicator
───────────────────────────────────────────── */
const Steps = ({ current }) => {
    const steps = [
        { n: 1, label: 'Select PRs' },
        { n: 2, label: 'Line Items' },
        { n: 3, label: 'Quotations & Order' },
    ]
    return (
        <div className='flex items-center gap-0 mb-6'>
            {steps.map((s, i) => (
                <React.Fragment key={s.n}>
                    <div className='flex flex-col items-center'>
                        <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all
                            ${current >= s.n
                                    ? 'bg-purple-600 text-white shadow-md shadow-purple-200'
                                    : 'bg-gray-100 text-gray-400'}`}
                        >
                            {current > s.n ? '✓' : s.n}
                        </div>
                        <span className={`text-xs mt-1 font-medium whitespace-nowrap ${current >= s.n ? 'text-purple-700' : 'text-gray-400'}`}>
                            {s.label}
                        </span>
                    </div>
                    {i < steps.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-2 mb-4 transition-all ${current > s.n ? 'bg-purple-400' : 'bg-gray-200'}`} />
                    )}
                </React.Fragment>
            ))}
        </div>
    )
}

/* ─────────────────────────────────────────────
   Empty entry factory — single source of truth
───────────────────────────────────────────── */
const makeEntry = () => ({
    id: Date.now() + Math.random(),
    vendor: '',
    quotationNumber: '',
    quotationDetails: '',
    pdfFile: null,
    isOrderGiven: false,
})

/* ══════════════════════════════════════════════════════════
   CreateQuotation
══════════════════════════════════════════════════════════ */
const CreateQuotation = () => {
    const token = useSelector((store) => store.user?.token) || localStorage.getItem('token')
    const navigate = useNavigate()

    /* ── Step ── */
    const [step, setStep] = useState(1)

    /* ────────────────────────────────────────
       STEP 1 — Purchase Request selection
    ──────────────────────────────────────── */
    const [prSearch, setPrSearch] = useState('')
    const [prList, setPrList] = useState([])
    const [prLoading, setPrLoading] = useState(false)
    const [prError, setPrError] = useState('')
    const [selectedPRs, setSelectedPRs] = useState([])
    const [expandedPR, setExpandedPR] = useState(null)
    const [selectionWarn, setSelectionWarn] = useState('')
    const searchTimer = useRef(null)

    /* ────────────────────────────────────────
       STEP 2 — Line item selection
    ──────────────────────────────────────── */
    const [selectedItems, setSelectedItems] = useState({})

    /* ────────────────────────────────────────
       STEP 3 — Vendor quotation entries
    ──────────────────────────────────────── */
    const [vendors, setVendors] = useState([])
    const [vendorsLoading, setVLoading] = useState(false)
    const [vendorError, setVendorError] = useState('')
    const [quotationEntries, setQuotationEntries] = useState([makeEntry()])

    /* ── Submission ── */
    const [submitting, setSub] = useState(false)
    const [submitErr, setErr] = useState('')
    const [done, setDone] = useState(false)
    const [createdQuot, setCreated] = useState(null)

    // All selected PRs share the same OEM type (enforced in togglePR)
    const isOemOrder = selectedPRs.length > 0 && selectedPRs.every((pr) => !!pr.isOem)

    useEffect(() => {
        if (step === 3 && isOemOrder) {
            setQuotationEntries([{ ...makeEntry(), isOrderGiven: true }])
        } else if (step === 3 && !isOemOrder && quotationEntries.length === 1 && quotationEntries[0].isOrderGiven) {
            setQuotationEntries([makeEntry()]) // reset format if toggled off
        }
    }, [step, isOemOrder])

    /* ────────────────────────────────────────
       Fetch PRs (debounced)
    ──────────────────────────────────────── */
    const loadPRs = useCallback(async (search = '') => {
        setPrLoading(true)
        setPrError('')
        try {
            const data = await fetchPurchaseRequests(token, { search, limit: 20 })
            const docs = data?.data?.docs ?? data?.data ?? []
            setPrList(Array.isArray(docs) ? docs : [])
        } catch (e) {
            setPrError(e.message || 'Failed to load Purchase Requests.')
        } finally {
            setPrLoading(false)
        }
    }, [token])

    useEffect(() => { loadPRs() }, [loadPRs])

    const handlePrSearch = (val) => {
        setPrSearch(val)
        clearTimeout(searchTimer.current)
        searchTimer.current = setTimeout(() => loadPRs(val), 400)
    }

    /* ────────────────────────────────────────
       Fetch Vendors (lazy — step 3 only)
    ──────────────────────────────────────── */
    useEffect(() => {
        if (step !== 3 || vendors.length > 0) return
        const load = async () => {
            setVLoading(true)
            setVendorError('')
            try {
                const res = await fetch(`${API_URL}vendors`, { headers: authHeaders(token) })
                const data = await res.json()
                if (data.error === false) {
                    const list = data?.data?.docs ?? data?.data ?? []
                    setVendors(Array.isArray(list) ? list : [])
                } else {
                    setVendorError('Failed to load vendors.')
                }
            } catch {
                setVendorError('Failed to load vendors.')
            } finally {
                setVLoading(false)
            }
        }
        load()
    }, [step, token, vendors.length])

    /* ────────────────────────────────────────
       STEP 1 helpers
    ──────────────────────────────────────── */
    const togglePR = (pr) => {
        setSelectionWarn('')
        const exists = selectedPRs.find((p) => p._id === pr._id)
        if (exists) {
            const ids = new Set((pr.lineItems ?? []).map((li) => li._id))
            setSelectedItems((si) => {
                const next = { ...si }
                ids.forEach((id) => delete next[id])
                return next
            })
            setSelectedPRs((prev) => prev.filter((p) => p._id !== pr._id))
            return
        }
        // OEM and Non-OEM PRs cannot be mixed in one quotation
        if (selectedPRs.length > 0 && !!selectedPRs[0].isOem !== !!pr.isOem) {
            setSelectionWarn(
                `Cannot mix OEM and Non-OEM Purchase Requests. This quotation is for ${selectedPRs[0].isOem ? 'OEM' : 'Non-OEM'} PRs only.`
            )
            return
        }
        setSelectedPRs((prev) => [...prev, pr])
    }

    const isPRSelected = (id) => selectedPRs.some((p) => p._id === id)

    /* ────────────────────────────────────────
       STEP 2 helpers
    ──────────────────────────────────────── */
    const allLineItems = useCallback(() => {
        const seen = new Set()
        const items = []
        selectedPRs.forEach((pr) => {
            ; (pr.lineItems ?? []).forEach((li) => {
                if (!seen.has(li._id)) {
                    seen.add(li._id)
                    items.push({ ...li, prId: pr.prId, prObjectId: pr._id })
                }
            })
        })
        return items
    }, [selectedPRs])

    /**
     * availableQty = total PR qty minus already-quoted quantity.
     * If quotedQuantity is 0 or absent, all units are available.
     * The dropdown will show 1..availableQty.
     * If availableQty === 0 we still allow selection (1..total) per spec.
     */
    const getAvailableQty = (li) => {
        const total = li.quantity ?? 0
        const quoted = li.quotedQuantity ?? 0
        const available = total - quoted
        return { total, quoted, available }
    }

    const toggleLineItem = (li) => {
        const { available } = getAvailableQty(li)
        // Block selection when all quantity is already allocated
        if (available === 0) return
        setSelectedItems((prev) => {
            if (prev[li._id]) {
                const next = { ...prev }
                delete next[li._id]
                return next
            }
            return {
                ...prev,
                [li._id]: {
                    modelId: li.model?._id ?? li.model,
                    modelName: li.model?.modelName ?? '—',
                    uom: li.model?.uom ?? '—',
                    maxQty: available,
                    quantity: available,
                    specification: li.specification ?? null,
                    requiredDeliveryDate: li.requiredDeliveryDate ?? null,
                    lastDateOfOrderDelivery: li.lastDateOfOrderDelivery ?? null,
                    prObjectId: li.prObjectId,
                },
            }
        })
    }

    const updateQty = (liId, val) => {
        setSelectedItems((prev) => {
            if (!prev[liId]) return prev
            const max = prev[liId].maxQty
            const qty = Math.min(Math.max(1, Number(val) || 1), max)
            return { ...prev, [liId]: { ...prev[liId], quantity: qty } }
        })
    }

    const selectAllItems = () => {
        const next = {}
        allLineItems().forEach((li) => {
            const { available } = getAvailableQty(li)
            // Skip items with no pending quantity
            if (available === 0) return
            next[li._id] = {
                modelId: li.model?._id ?? li.model,
                modelName: li.model?.modelName ?? '—',
                uom: li.model?.uom ?? '—',
                maxQty: available,
                quantity: available,
                specification: li.specification ?? null,
                requiredDeliveryDate: li.requiredDeliveryDate ?? null,
                lastDateOfOrderDelivery: li.lastDateOfOrderDelivery ?? null,
                prObjectId: li.prObjectId,
            }
        })
        setSelectedItems(next)
    }

    const clearAllItems = () => setSelectedItems({})

    /* ────────────────────────────────────────
       STEP 3 helpers
    ──────────────────────────────────────── */
    const addEntry = () => {
        if (isOemOrder) return;
        setQuotationEntries((p) => [...p, makeEntry()])
    }

    const removeEntry = (id) =>
        setQuotationEntries((p) => p.filter((e) => e.id !== id))

    const updateEntry = (id, field, val) =>
        setQuotationEntries((p) =>
            p.map((e) => (e.id === id ? { ...e, [field]: val } : e))
        )

    const toggleOrderGiven = (id) => {
        setQuotationEntries((p) =>
            p.map((e) => ({
                ...e,
                isOrderGiven: e.id === id ? !e.isOrderGiven : false,
            }))
        )
    }

    /* ────────────────────────────────────────
       Guards
    ──────────────────────────────────────── */
    const canGoStep2 = selectedPRs.length > 0
    const canGoStep3 = Object.keys(selectedItems).length > 0

    const validate = () => {
        if (!isOemOrder && quotationEntries.length < 2)
            return 'Please add at least 2 vendor quotations for comparison.'
        if (quotationEntries.some((e) => !e.vendor))
            return 'Each quotation entry must have a vendor selected.'
        
        const winningCount = quotationEntries.filter((e) => e.isOrderGiven).length
        if (winningCount !== 1) {
            return 'You must select exactly one vendor as the winning vendor (Mark as Order Given).'
        }
        return null
    }

    /* ────────────────────────────────────────
       Update quotedQuantity on each PR
    ──────────────────────────────────────── */
    const updatePRQuotedQuantities = async (prs, selItems) => {
        // Build a map of { prId -> { lineItemId -> newQuotedQuantity } } for local state sync
        const updatedQtyMap = {}

        const patchPromises = prs.map((pr) => {
            const lineItemUpdates = (pr.lineItems ?? [])
                .filter((li) => selItems[li._id])
                .map((li) => {
                    const selectedQty = Number(selItems[li._id].quantity) || 0
                    const currentQuoted = Number(li.quotedQuantity ?? 0)
                    const currentTotal = Number(li.quantity ?? 0)
                    const newQuoted = Math.min(currentQuoted + selectedQty, currentTotal)

                    if (!updatedQtyMap[pr._id]) updatedQtyMap[pr._id] = {}
                    updatedQtyMap[pr._id][li._id] = newQuoted

                    return {
                        _id: li._id,
                        quotedQuantity: newQuoted,
                    }
                })

            if (lineItemUpdates.length === 0) return Promise.resolve()

            return fetch(`${API_URL}purchase-requests/${pr._id}`, {
                method: 'PATCH',
                headers: authHeaders(token),
                body: JSON.stringify({ lineItems: lineItemUpdates }),
            }).catch((err) => {
                console.warn(`Failed to update quotedQuantity for PR ${pr.prId}:`, err)
            })
        })

        await Promise.allSettled(patchPromises)

        // Sync updated quotedQuantity back into local state so subsequent renders
        // (and a second quotation attempt) see the correct allocated quantities.
        const applyUpdates = (prArray) =>
            prArray.map((pr) => {
                if (!updatedQtyMap[pr._id]) return pr
                return {
                    ...pr,
                    lineItems: (pr.lineItems ?? []).map((li) =>
                        updatedQtyMap[pr._id][li._id] !== undefined
                            ? { ...li, quotedQuantity: updatedQtyMap[pr._id][li._id] }
                            : li
                    ),
                }
            })

        setSelectedPRs((prev) => applyUpdates(prev))
        setPrList((prev) => applyUpdates(prev))
    }

    const handleSubmit = async () => {
        setErr('')
        const errMsg = validate()
        if (errMsg) { setErr(errMsg); return }

        setSub(true)
        try {
            const formData = new FormData()

            selectedPRs.forEach((pr) => formData.append('purchaseRequests[]', pr._id))
            formData.append('isOem', String(isOemOrder))

            const lineItemsPayload = Object.values(selectedItems).map((li) => ({
                model: li.modelId,
                quantity: li.quantity,
                specification: li.specification ?? null,
                requiredDeliveryDate: li.requiredDeliveryDate,
                lastDateOfOrderDelivery: li.lastDateOfOrderDelivery ?? null,
            }))
            formData.append('lineItems', JSON.stringify(lineItemsPayload))

            const winningEntry = quotationEntries.find((e) => e.isOrderGiven) ?? null
            const receivedEntries = quotationEntries.filter((e) => !e.isOrderGiven)

            const quotationsPayload = receivedEntries.map((e) => ({
                vendor: e.vendor,
                quotationNumber: e.quotationNumber.trim() || null,
                quotationDetails: e.quotationDetails.trim() || null,
            }))
            formData.append('quotations', JSON.stringify(quotationsPayload))

            receivedEntries.forEach((e, i) => {
                if (e.pdfFile) formData.append(`pdfs[${i}]`, e.pdfFile)
            })

            if (winningEntry) {
                formData.append('orderGivenToVendor', winningEntry.vendor)
                if (winningEntry.quotationNumber)
                    formData.append('orderGivenQuotationNumber', winningEntry.quotationNumber.trim())
                if (winningEntry.pdfFile)
                    formData.append('orderPdf', winningEntry.pdfFile)
            }

            const res = await fetch(`${API_URL}quotations`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            })
            const result = await res.json()

            if (result.error === false) {
                await updatePRQuotedQuantities(selectedPRs, selectedItems)
                setCreated(result.data ?? result)
                setDone(true)
            } else {
                setErr(result.message || 'Failed to create quotation.')
            }
        } catch (e) {
            setErr(e.message || 'Something went wrong. Please try again.')
        } finally {
            setSub(false)
        }
    }

    /* ────────────────────────────────────────
       Reset
    ──────────────────────────────────────── */
    const resetForm = () => {
        setStep(1)
        setSelectedPRs([])
        setSelectedItems({})
        setQuotationEntries([makeEntry()])
        setDone(false)
        setCreated(null)
        setErr('')
        setSelectionWarn('')
        setPrSearch('')
    }

    /* ══════════════════════════════════════════
       Success Screen
    ══════════════════════════════════════════ */
    if (done) {
        return (
            <div>
                <PageHeader pageName='CREATE QUOTATION' breadcrump1='Home' breadcrump2='Quotation' link='/dashboard' />
                <div className='main lg:p-6 xl:p-6 2xl:p-6 sm:p-3 md:p-3'>
                    <div className='text-center py-14 bg-green-50 border border-green-200 rounded-xl mt-4 px-6'>
                        <FontAwesomeIcon icon={faCheckCircle} className='text-green-500 mb-4' size='4x' />
                        <h4 className='text-green-800 font-bold mb-2'>Quotation Created!</h4>
                        {createdQuot?._id && (
                            <p className='text-green-700 text-sm mb-1'>
                                ID: <strong>{createdQuot._id}</strong>
                            </p>
                        )}
                        <p className='text-gray-500 text-sm mb-1'>
                            Mapped against <strong>{selectedPRs.length}</strong> Purchase Request(s)
                        </p>
                        <div className='flex justify-center mb-2'>
                            <OemBadge isOem={isOemOrder} />
                        </div>
                        <p className='text-gray-500 text-sm mb-6'>
                            Quotation has been successfully submitted.
                        </p>
                        <div className='flex justify-center gap-3'>
                            <button className='btn add-btn text-white px-5 font-bold' onClick={resetForm}>
                                Create Another
                            </button>
                            <button className='btn btn-light border px-5 font-bold' onClick={() => navigate('/purchase/view_quotations')}>
                                View All Quotations
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    /* ════════════════════════════════════════
       Derived values used in render
    ════════════════════════════════════════ */
    const items = allLineItems()
    const selectedItemCount = Object.keys(selectedItems).length
    const winningEntry = quotationEntries.find((e) => e.isOrderGiven) ?? null

    /* ══════════════════════════════════════════
       Main UI
    ══════════════════════════════════════════ */
    return (
        <div>
            <PageHeader pageName='CREATE QUOTATION' breadcrump1='Home' breadcrump2='Quotation' link='/dashboard' />

            <div className='main lg:p-6 xl:p-6 2xl:p-6 sm:p-3 md:p-3'>

                {/* Back */}
                <button
                    onClick={() => navigate('/purchase/view_quotations')}
                    className='btn btn-light border mb-4 text-sm font-semibold flex items-center gap-2'
                >
                    <FontAwesomeIcon icon={faChevronLeft} /> View All Quotations
                </button>

                <Steps current={step} />

                {/* ══════════════════════════
                    STEP 1 — Select PRs
                ══════════════════════════ */}
                {step === 1 && (
                    <div>
                        <div className='bg-white border border-gray-200 rounded-xl p-5 mb-4 shadow-sm'>
                            <div className='flex items-center gap-3 mb-4'>
                                <div className='w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0'>
                                    <FontAwesomeIcon icon={faClipboardList} className='text-blue-600 text-xl' />
                                </div>
                                <div>
                                    <h5 className='text-gray-800 font-bold mb-0'>Select Purchase Requests</h5>
                                    <p className='text-gray-500 text-xs mb-0'>
                                        Choose one or more PRs of the same type (OEM or Non-OEM) whose materials will be purchased together
                                    </p>
                                </div>
                            </div>

                            {/* Search */}
                            <div className='relative mb-4'>
                                <input
                                    type='text'
                                    className='form-control pl-9'
                                    placeholder='Search by PR ID…'
                                    value={prSearch}
                                    onChange={(e) => handlePrSearch(e.target.value)}
                                />
                            </div>

                            {/* Selected pills */}
                            {selectedPRs.length > 0 && (
                                <div className='flex flex-wrap gap-2 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100'>
                                    <span className='text-xs font-semibold text-blue-700 mr-1 self-center'>Selected:</span>
                                    <OemBadge isOem={isOemOrder} />
                                    {selectedPRs.map((pr) => (
                                        <span key={pr._id} className='flex items-center gap-1.5 text-xs bg-blue-600 text-white px-3 py-1 rounded-full font-semibold'>
                                            {pr.prId}
                                            <span className={`text-[10px] font-bold px-1.5 py-0 rounded-full ${pr.isOem ? 'bg-purple-200 text-purple-800' : 'bg-white/25 text-white'}`}>
                                                {pr.isOem ? 'OEM' : 'Non-OEM'}
                                            </span>
                                            <button onClick={() => togglePR(pr)} className='ml-0.5 hover:opacity-70'>
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            {selectionWarn && (
                                <div className='flex items-center gap-2 rounded-lg px-4 py-3 mb-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm'>
                                    <FontAwesomeIcon icon={faExclamationTriangle} /> {selectionWarn}
                                </div>
                            )}

                            {prError && (
                                <div className='flex items-center gap-2 rounded-lg px-4 py-3 mb-3 bg-red-50 border border-red-200 text-red-700 text-sm'>
                                    <FontAwesomeIcon icon={faExclamationTriangle} /> {prError}
                                </div>
                            )}

                            {/* PR list */}
                            {prLoading ? (
                                <div className='text-center py-10 text-gray-400'>
                                    <FontAwesomeIcon icon={faSpinner} spin className='text-2xl mb-2' />
                                    <p className='text-sm'>Loading Purchase Requests…</p>
                                </div>
                            ) : prList.length === 0 ? (
                                <div className='text-center py-10 text-gray-400'>
                                    <FontAwesomeIcon icon={faBoxOpen} className='text-3xl mb-2' />
                                    <p className='text-sm'>No Purchase Requests found.</p>
                                </div>
                            ) : (
                                <div className='space-y-3 max-h-[420px] overflow-y-auto pr-1'>
                                    {prList.map((pr) => {
                                        const selected = isPRSelected(pr._id)
                                        const expanded = expandedPR === pr._id
                                        return (
                                            <div
                                                key={pr._id}
                                                className={`rounded-xl border-2 transition-all ${selected
                                                    ? 'border-blue-500 bg-blue-50'
                                                    : 'border-gray-200 bg-white hover:border-gray-300'}`}
                                            >
                                                <div className='flex items-center gap-3 px-4 py-3'>
                                                    <input
                                                        type='checkbox'
                                                        checked={selected}
                                                        onChange={() => togglePR(pr)}
                                                        className='w-4 h-4 accent-blue-600 cursor-pointer flex-shrink-0'
                                                    />
                                                    <div className='flex-1 min-w-0'>
                                                        <div className='flex flex-wrap items-center gap-2'>
                                                            <span className='font-bold text-sm text-gray-800'>{pr.prId}</span>
                                                            <Badge color={statusColor(pr.approvalStatus)}>
                                                                {pr.approvalStatus ?? 'N/A'}
                                                            </Badge>
                                                            <OemBadge isOem={!!pr.isOem} />
                                                        </div>
                                                        <div className='text-xs text-gray-500 mt-0.5 flex gap-3 flex-wrap'>
                                                            <span>By: <b>{pr.requestRaisedBy ?? pr.createdBy?.name ?? '—'}</b></span>
                                                            <span>Items: <b>{pr.lineItems?.length ?? 0}</b></span>
                                                            {pr.date && <span>{new Date(pr.date).toLocaleDateString()}</span>}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => setExpandedPR(expanded ? null : pr._id)}
                                                        className='text-gray-400 hover:text-gray-600 text-sm px-2 flex-shrink-0'
                                                    >
                                                        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
                                                    </button>
                                                </div>

                                                {/* Expandable line items preview */}
                                                {expanded && (
                                                    <div className='border-t border-gray-200 px-4 pb-3 pt-2'>
                                                        <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2'>Line Items</p>
                                                        <div className='overflow-x-auto'>
                                                            <table className='text-xs w-full'>
                                                                <thead>
                                                                    <tr className='text-gray-400'>
                                                                        <th className='text-left pb-1 pr-4 font-semibold'>Model</th>
                                                                        <th className='text-right pb-1 font-semibold'>Qty</th>
                                                                        <th className='text-left pb-1 pl-2 font-semibold'>UOM</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {(pr.lineItems ?? []).map((li) => (
                                                                        <tr key={li._id} className='border-t border-gray-100'>
                                                                            <td className='py-1 pr-4 text-gray-700 font-medium'>{li.model?.modelName ?? '—'}</td>
                                                                            <td className='py-1 text-right font-bold text-gray-800'>{li.quantity}</td>
                                                                            <td className='py-1 pl-2 text-gray-500'>{li.model?.uom ?? '—'}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>

                        <div className='flex justify-end'>
                            <button
                                className='btn add-btn text-white px-6 font-bold flex items-center gap-2'
                                disabled={!canGoStep2}
                                onClick={() => setStep(2)}
                            >
                                Next: Select Line Items
                                <FontAwesomeIcon icon={faChevronDown} className='rotate-[-90deg]' />
                            </button>
                        </div>
                    </div>
                )}

                {/* ══════════════════════════
                    STEP 2 — Line Items
                ══════════════════════════ */}
                {step === 2 && (
                    <div>
                        <div className='bg-white border border-gray-200 rounded-xl p-5 mb-4 shadow-sm'>
                            <div className='flex items-center justify-between mb-4 flex-wrap gap-3'>
                                <div className='flex items-center gap-3'>
                                    <div className='w-11 h-11 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0'>
                                        <FontAwesomeIcon icon={faLayerGroup} className='text-orange-600 text-xl' />
                                    </div>
                                    <div>
                                        <h5 className='text-gray-800 font-bold mb-0'>Select Line Items</h5>
                                        <p className='text-gray-500 text-xs mb-0'>Choose items and adjust quantities for this quotation</p>
                                    </div>
                                </div>
                                <div className='flex gap-2 items-center flex-wrap'>
                                    <OemBadge isOem={isOemOrder} />
                                    <span className='text-xs text-gray-500 font-medium'>{selectedItemCount} / {items.length} selected</span>
                                    <button className='text-xs text-blue-600 font-semibold hover:underline' onClick={selectAllItems}>Select All</button>
                                    <span className='text-gray-300'>|</span>
                                    <button className='text-xs text-red-500 font-semibold hover:underline' onClick={clearAllItems}>Clear</button>
                                </div>
                            </div>

                            {selectedPRs.map((pr) => {
                                const prItems = items.filter((li) => li.prObjectId === pr._id)
                                return (
                                    <div key={pr._id} className='mb-5'>
                                        <div className='flex items-center gap-2 mb-2'>
                                            <span className='text-xs font-bold text-gray-600 uppercase tracking-wider'>{pr.prId}</span>
                                            <Badge color={statusColor(pr.approvalStatus)}>{pr.approvalStatus}</Badge>
                                            <OemBadge isOem={!!pr.isOem} />
                                        </div>
                                        <div className='rounded-xl border border-gray-200 overflow-hidden'>
                                            <table className='w-full text-sm'>
                                                <thead>
                                                    <tr className='bg-gray-50 text-gray-500 text-xs uppercase tracking-wide'>
                                                        <th className='text-left py-2 px-4 w-8'></th>
                                                        <th className='text-left py-2 px-4'>Model</th>
                                                        <th className='text-left py-2 px-4'>UOM</th>
                                                        <th className='text-left py-2 px-4 hidden md:table-cell'>Part Code</th>
                                                        <th className='text-right py-2 px-4'>Qty</th>
                                                        {/* ── MERGED COLUMN ── */}
                                                        <th className='text-center py-2 px-4'>Qty for Quotation</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {prItems.map((li) => {

                                                        const isChecked = !!selectedItems[li._id]
                                                        const { total, available } = getAvailableQty(li)
                                                        const isFullyAllocated = available === 0

                                                        // Build dropdown options 1..available
                                                        const options = Array.from(
                                                            { length: available },
                                                            (_, i) => i + 1
                                                        )

                                                        return (
                                                            <tr
                                                                key={li._id}
                                                                className={`border-t border-gray-100 transition-colors
                                                                    ${isFullyAllocated
                                                                        ? 'bg-gray-50 opacity-60 cursor-not-allowed'
                                                                        : isChecked
                                                                            ? 'bg-orange-50 cursor-pointer'
                                                                            : 'hover:bg-gray-50 cursor-pointer'
                                                                    }`}
                                                                onClick={() => !isFullyAllocated && toggleLineItem(li)}
                                                            >
                                                                <td className='py-2.5 px-4'>
                                                                    <input
                                                                        type='checkbox'
                                                                        checked={isChecked}
                                                                        disabled={isFullyAllocated}
                                                                        onChange={() => toggleLineItem(li)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className={`w-4 h-4 accent-orange-500 ${isFullyAllocated ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                                    />
                                                                </td>
                                                                <td className='py-2.5 px-4 font-medium text-gray-800'>
                                                                    {li.model?.modelName ?? '—'}
                                                                    {isFullyAllocated && (
                                                                        <span className='ml-2 text-xs font-semibold text-red-500 bg-red-50 px-1.5 py-0.5 rounded-full'>Fully Allocated</span>
                                                                    )}
                                                                </td>
                                                                <td className='py-2.5 px-4 text-gray-500 text-xs'>{li?.model?.uom ?? '—'}</td>
                                                                <td className='py-2.5 px-4 text-gray-500 text-xs hidden md:table-cell'>{li?.model?.partCode ?? '—'}</td>
                                                                <td className='py-2.5 px-4 text-right text-gray-500'>{total}</td>

                                                                {/* ── MERGED CELL: dropdown + available label ── */}
                                                                <td
                                                                    className='py-2.5 px-4 text-center'
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <div className='flex flex-col items-center gap-1'>
                                                                        {isFullyAllocated ? (
                                                                            <span className='text-xs text-red-500 font-semibold'>Pending: 0</span>
                                                                        ) : (
                                                                            <select
                                                                                disabled={!isChecked}
                                                                                value={isChecked ? selectedItems[li._id].quantity : available}
                                                                                onChange={(e) => updateQty(li._id, e.target.value)}
                                                                                className={`form-select px-2 py-1 text-sm font-bold transition-all text-center
                                                                                    ${isChecked
                                                                                        ? 'border-orange-400 bg-white text-gray-800 cursor-pointer'
                                                                                        : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                                                                    }`}
                                                                                style={{ minWidth: '70px', display: 'inline-block' }}
                                                                            >
                                                                                {options.map((n) => (
                                                                                    <option key={n} value={n}>{n}</option>
                                                                                ))}
                                                                            </select>
                                                                        )}
                                                                        <div className='small text-muted mt-1'>
                                                                            Allocated: {Number(li?.quotedQuantity ?? 0)} / {total} | Pending: {available}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        <div className='flex justify-between mb-6'>
                            <button className='btn btn-light border px-5 font-semibold flex items-center gap-2' onClick={() => setStep(1)}>
                                <FontAwesomeIcon icon={faChevronLeft} /> Back
                            </button>
                            {/* Hide the Next button when there are no selectable items (all pending qty = 0) */}
                            {items.some((li) => getAvailableQty(li).available > 0) && (
                                <button
                                    className='btn add-btn text-white px-6 font-bold flex items-center gap-2'
                                    disabled={!canGoStep3}
                                    onClick={() => setStep(3)}
                                >
                                    Next: Add Quotations
                                    <FontAwesomeIcon icon={faChevronDown} className='rotate-[-90deg]' />
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* ══════════════════════════
                    STEP 3 — Vendor Quotations + Order Given To
                ══════════════════════════ */}
                {step === 3 && (
                    <div>
                        {/* Summary bar */}
                        <div className='flex flex-wrap gap-3 mb-4'>
                            <div className='flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm'>
                                <FontAwesomeIcon icon={faClipboardList} className='text-blue-500' />
                                <span className='text-blue-700 font-semibold'>
                                    {selectedPRs.length} PR{selectedPRs.length !== 1 ? 's' : ''}
                                </span>
                                <span className='text-blue-400 text-xs'>({selectedPRs.map((p) => p.prId).join(', ')})</span>
                            </div>
                            {isOemOrder ? (
                                <div className='flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-4 py-2 text-sm'>
                                    <FontAwesomeIcon icon={faTrophy} className='text-purple-600' />
                                    <span className='text-purple-800 font-semibold'>OEM Order (Direct Purchase)</span>
                                </div>
                            ) : (
                                <div className='flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm'>
                                    <OemBadge isOem={false} />
                                    <span className='text-gray-700 font-semibold'>Non-OEM — vendor comparison required</span>
                                </div>
                            )}
                            <div className='flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-4 py-2 text-sm'>
                                <FontAwesomeIcon icon={faLayerGroup} className='text-orange-500' />
                                <span className='text-orange-700 font-semibold'>
                                    {selectedItemCount} line item{selectedItemCount !== 1 ? 's' : ''}
                                </span>
                            </div>
                            {winningEntry && (
                                <div className='flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm'>
                                    <FontAwesomeIcon icon={faTrophy} className='text-green-500' />
                                    <span className='text-green-700 font-semibold'>Order given to 1 vendor</span>
                                </div>
                            )}
                        </div>

                        {/* Instruction callout */}
                        <div className='flex items-start gap-3 bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 mb-4 text-sm text-purple-800'>
                            <FontAwesomeIcon icon={faTrophy} className='text-purple-400 mt-0.5 flex-shrink-0' />
                            <p className='mb-0'>
                                {isOemOrder ? (
                                    <>Since this is an <strong>OEM Order</strong>, please select the single vendor. It will be automatically marked as <em>Order Given</em>.</>
                                ) : (
                                    <>Add <strong>all received vendor quotations</strong> below.
                                    Use <strong>"Mark as Order Given"</strong> on the winning vendor — that entry is saved
                                    to <em>Order Given To</em> separately. All other entries are saved as received quotations.</>
                                )}
                            </p>
                        </div>

                        <div className='bg-white border border-gray-200 rounded-xl p-5 mb-4 shadow-sm'>
                            <div className='flex items-center justify-between mb-5 flex-wrap gap-3'>
                                <div className='flex items-center gap-3'>
                                    <div className='w-11 h-11 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0'>
                                        <FontAwesomeIcon icon={faStore} className='text-purple-600 text-xl' />
                                    </div>
                                    <div>
                                        <h5 className='text-gray-800 font-bold mb-0'>Vendor Quotations</h5>
                                        <p className='text-gray-500 text-xs mb-0'>Add all quotations received from vendors</p>
                                    </div>
                                </div>
                                {!isOemOrder && (
                                    <button
                                        className='btn add-btn text-white px-4 text-sm flex items-center gap-2 font-semibold'
                                        onClick={addEntry}
                                    >
                                        <FontAwesomeIcon icon={faPlus} /> Add Vendor
                                    </button>
                                )}
                            </div>

                            {vendorError && (
                                <div className='flex items-center gap-2 rounded-lg px-4 py-3 mb-4 bg-red-50 border border-red-200 text-red-700 text-sm'>
                                    <FontAwesomeIcon icon={faExclamationTriangle} /> {vendorError}
                                </div>
                            )}

                            {vendorsLoading ? (
                                <div className='text-center py-10 text-gray-400'>
                                    <FontAwesomeIcon icon={faSpinner} spin className='text-2xl mb-2' />
                                    <p className='text-sm'>Loading vendors…</p>
                                </div>
                            ) : (
                                <div className='space-y-4'>
                                    {quotationEntries.map((entry) => (
                                        <div
                                            key={entry.id}
                                            className={`rounded-xl border-2 p-4 transition-all ${entry.isOrderGiven
                                                ? 'border-green-400 bg-green-50'
                                                : 'border-gray-200 bg-gray-50'
                                                }`}
                                        >
                                            {/* Card header */}
                                            <div className='flex items-center justify-between mb-3 flex-wrap gap-2'>
                                                <div className='flex items-center gap-2'>
                                                    {entry.isOrderGiven ? (
                                                        <span className='flex items-center gap-1 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full font-bold'>
                                                            <FontAwesomeIcon icon={faTrophy} /> Order Given To
                                                        </span>
                                                    ) : (
                                                        <span className='text-xs font-bold text-gray-500 uppercase tracking-wider'>
                                                            Received Quotation
                                                        </span>
                                                    )}
                                                </div>
                                                <div className='flex items-center gap-3'>
                                                    {!isOemOrder && (
                                                        <button
                                                            type='button'
                                                            onClick={() => toggleOrderGiven(entry.id)}
                                                            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${entry.isOrderGiven
                                                                ? 'bg-green-500 text-white border-green-500'
                                                                : 'bg-white text-gray-600 border-gray-300 hover:border-green-400 hover:text-green-600'
                                                                }`}
                                                            title='Mark this as the vendor the order was placed with'
                                                        >
                                                            <FontAwesomeIcon icon={faTrophy} />
                                                            {entry.isOrderGiven ? 'Order Given ✓' : 'Mark as Order Given'}
                                                        </button>
                                                    )}
                                                    {!isOemOrder && quotationEntries.length > 1 && (
                                                        <button
                                                            onClick={() => removeEntry(entry.id)}
                                                            className='text-red-400 hover:text-red-600 text-sm transition-colors'
                                                            title='Remove entry'
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Fields — 2-col grid */}
                                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>

                                                {/* Vendor */}
                                                <div>
                                                    <label className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block'>
                                                        Vendor <span className='text-danger'>*</span>
                                                    </label>
                                                    <select
                                                        className='form-select'
                                                        value={entry.vendor}
                                                        onChange={(e) => updateEntry(entry.id, 'vendor', e.target.value)}
                                                    >
                                                        <option value=''>— Select Vendor —</option>
                                                        {vendors.map((v) => (
                                                            <option key={v._id} value={v._id}>{v.vendorName}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Quotation Number */}
                                                <div>
                                                    <label className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block'>
                                                        Quotation Number{' '}
                                                        <span className='text-gray-400 font-normal'>(optional for oem)</span>
                                                    </label>
                                                    <div className='relative'>
                                                        <input
                                                            type='text'
                                                            className='form-control pl-8 text-sm'
                                                            value={entry.quotationNumber}
                                                            onChange={(e) => updateEntry(entry.id, 'quotationNumber', e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* PDF upload */}
                                                <div>
                                                    <label className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block'>
                                                        PDF Upload{' '}
                                                        <span className='text-gray-400 font-normal'>
                                                            {entry.isOrderGiven ? '(order PDF — saved to orders-given/)' : '(optional for oem)'}
                                                        </span>
                                                    </label>
                                                    <input
                                                        type='file'
                                                        accept='application/pdf'
                                                        className='form-control text-sm'
                                                        onChange={(e) => updateEntry(entry.id, 'pdfFile', e.target.files?.[0] ?? null)}
                                                    />
                                                    {entry.pdfFile && (
                                                        <p className='text-xs text-green-600 mt-1 font-medium flex items-center gap-1'>
                                                            <FontAwesomeIcon icon={faFilePdf} /> {entry.pdfFile.name}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Quotation details */}
                                                {!entry.isOrderGiven && (
                                                    <div className='md:col-span-2'>
                                                        <label className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block'>
                                                            Quotation Details
                                                        </label>
                                                        <textarea
                                                            className='form-control'
                                                            rows={3}
                                                            value={entry.quotationDetails}
                                                            onChange={(e) => updateEntry(entry.id, 'quotationDetails', e.target.value)}
                                                            maxLength={1000}
                                                        />
                                                        <div className='text-xs text-gray-400 mt-1 text-right'>
                                                            {entry.quotationDetails.length} / 1000
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Error banner */}
                        {submitErr && (
                            <div className='flex items-center gap-2 rounded-lg px-4 py-3 mb-4 bg-red-50 border border-red-200 text-red-700 text-sm'>
                                <FontAwesomeIcon icon={faExclamationTriangle} /> {submitErr}
                            </div>
                        )}

                        {/* Navigation */}
                        <div className='flex justify-between mb-6'>
                            <button className='btn btn-light border px-5 font-semibold flex items-center gap-2' onClick={() => setStep(2)}>
                                <FontAwesomeIcon icon={faChevronLeft} /> Back
                            </button>
                            <button
                                className='btn add-btn text-white px-6 font-bold flex items-center gap-2'
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting
                                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Creating…</>
                                    : <><FontAwesomeIcon icon={faFileAlt} /> Create Quotation</>
                                }
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    )
}

export default CreateQuotation